import { Liability, RealEstateOwned } from '../../models/mortgage.model';
import { MortgageApplication } from '../../models/mortgage-app.model';
import { activeOnly, removeOrMarkDeleted } from '../../utils/entity-state';
import { isFeatureOwned, MORTGAGE_LIABILITY_TYPE, nextPlaceholderLiabilityId } from './reo-liability-sync';

/**
 * LLS-569 item 3: the "Add Existing Lien" modal stores each lien as a real MortgageLoan
 * liability linked to its REO. These helpers own WHERE a lien lives and how the REO's
 * amountOfMortgage is re-derived; the components only orchestrate.
 *
 * Storage mirrors reo-liability-sync.ts (which the shared urla2020 save already understands):
 *   - a REO that has not been saved yet (reoId null or <= 0) nests its liens in reo.liabilities;
 *     the backend stamps the REO's real id onto them on save.
 *   - a persisted REO (reoId > 0) keeps its liens in mortgage.liabilities with liability.reoId set.
 * Removal follows LLS-520: a persisted row is marked entityState 'Deleted' (the save only deletes
 * rows carrying that marker), an unsaved row is simply spliced.
 * reo-liability-sync.ts's own auto-created placeholder (isFeatureOwned) is never a lien here.
 */
/** Re-exported under the old name for specs that still import it from here. */
export const MORTGAGE_LIEN_TYPE = MORTGAGE_LIABILITY_TYPE;

/** The fields the lien modal edits; everything else on an existing liability is left untouched. */
const EDITABLE_LIEN_FIELDS: ReadonlyArray<keyof Liability> = [
  'lienPosition', 'unpaidBalance', 'holderName', 'accountNumber', 'owningBorrowerIds',
];

export function isNewReo(reo: RealEstateOwned): boolean {
  return reo.reoId == null || reo.reoId <= 0;
}

/**
 * Active MortgageLoan liabilities linked to this REO — top-level by reoId, or nested — without
 * duplicates, and never including reo-liability-sync's own auto-created placeholder.
 */
export function getReoLiens(mortgage: MortgageApplication, reo: RealEstateOwned): Liability[] {
  const topLevel = activeOnly(mortgage?.liabilities).filter(
    l => l.typeOfLiability === MORTGAGE_LIABILITY_TYPE && l.reoId != null && l.reoId === reo.reoId
  );
  const nested = activeOnly(reo?.liabilities).filter(l => l.typeOfLiability === MORTGAGE_LIABILITY_TYPE);
  const seen = new Set<number>();
  return [...topLevel, ...nested].filter(l => {
    const key = l.liabilityId > 0 ? l.liabilityId : undefined;
    if (key !== undefined) {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }
    return true; // unsaved liens (id <= 0 / missing) are unique by construction
  }).filter((l, i, arr) => arr.indexOf(l) === i)
    .filter(l => !isFeatureOwned(l));
}

/** A new lien belongs to the REO's owners by default, or to every borrower when the REO has none. */
export function defaultLienOwnerIds(mortgage: MortgageApplication, reo: RealEstateOwned): number[] {
  const reoOwners = (reo.owningBorrowerIds ?? []).filter(id => id != null);
  if (reoOwners.length > 0) {
    return [...reoOwners];
  }
  return (mortgage.borrowers ?? []).map(b => b.borrowerId).filter(id => id != null) as number[];
}

export function createLien(reo: RealEstateOwned, owningBorrowerIds: number[]): Liability {
  const lien = new Liability();
  lien.liabilityId = nextPlaceholderLiabilityId();
  lien.typeOfLiability = MORTGAGE_LIABILITY_TYPE;
  lien.reoId = reo.reoId;
  lien.owningBorrowerIds = [...owningBorrowerIds];
  return lien;
}

/** The modal edits a copy so Cancel leaves the stored lien untouched; addOrUpdateLien merges it back by id. */
export function cloneLienForEdit(lien: Liability): Liability {
  const clone = Object.assign(new Liability(), lien);
  clone.owningBorrowerIds = [...(lien.owningBorrowerIds ?? [])];
  return clone;
}

export function addOrUpdateLien(mortgage: MortgageApplication, reo: RealEstateOwned, lien: Liability): void {
  mortgage.liabilities = mortgage.liabilities ?? [];
  reo.liabilities = reo.liabilities ?? [];
  lien.typeOfLiability = MORTGAGE_LIABILITY_TYPE;
  lien.reoId = reo.reoId;

  const existing = getReoLiens(mortgage, reo).find(l => l.liabilityId === lien.liabilityId);
  if (existing) {
    if (existing !== lien) {
      for (const field of EDITABLE_LIEN_FIELDS) {
        (existing as any)[field] = (lien as any)[field];
      }
    }
    return;
  }
  (isNewReo(reo) ? reo.liabilities : mortgage.liabilities).push(lien);
}

export function removeLien(mortgage: MortgageApplication, reo: RealEstateOwned, lien: Liability): void {
  if (mortgage.liabilities?.includes(lien)) {
    removeOrMarkDeleted(mortgage.liabilities, lien, lien.liabilityId);
  }
  if (reo.liabilities?.includes(lien)) {
    removeOrMarkDeleted(reo.liabilities, lien, lien.liabilityId);
  }
}

/**
 * When an REO is removed, its top-level liens stay on the URLA but must stop pointing at it
 * (mirrors reo-step's REO delete). Nested liens leave with the REO. Returns the liabilityIds it
 * unlinked so a caller can re-link them if the same REO is revived later in the session.
 */
export function unlinkReoLiens(mortgage: MortgageApplication, reo: RealEstateOwned): number[] {
  if (reo.reoId == null) {
    return [];
  }
  const unlinkedIds: number[] = [];
  activeOnly(mortgage?.liabilities).forEach(l => {
    if (l.reoId === reo.reoId) {
      l.reoId = null as any;
      unlinkedIds.push(l.liabilityId);
    }
  });
  return unlinkedIds;
}

/** Re-points previously unlinked liens at a revived REO (the inverse of unlinkReoLiens for the same session). */
export function relinkReoLiens(mortgage: MortgageApplication, reo: RealEstateOwned, liabilityIds: number[]): void {
  if (reo.reoId == null || !liabilityIds.length) {
    return;
  }
  activeOnly(mortgage?.liabilities).forEach(l => {
    if (l.reoId == null && liabilityIds.includes(l.liabilityId)) {
      l.reoId = reo.reoId;
    }
  });
}

/** Api underwriting and document extraction still read reo.amountOfMortgage, so keep it equal to the lien total. */
export function recalculateAmountOfMortgage(mortgage: MortgageApplication, reo: RealEstateOwned): void {
  const liens = getReoLiens(mortgage, reo);
  reo.amountOfMortgage = liens.length
    ? liens.reduce((sum, l) => sum + (Number(l.unpaidBalance) || 0), 0)
    : undefined;
}

/** A stable fingerprint of the REO's real liens (ids + balances) so a caller can tell whether a dialog session changed them. */
export function reoLienFingerprint(mortgage: MortgageApplication, reo: RealEstateOwned): string {
  return getReoLiens(mortgage, reo)
    .map(l => `${l.liabilityId}:${Number(l.unpaidBalance) || 0}`)
    .sort()
    .join('|');
}

export function maskAccountNumber(accountNumber: string | null | undefined): string {
  const value = (accountNumber ?? '').trim();
  if (value.length <= 4) {
    return value;
  }
  return '****' + value.slice(-4);
}
